import { Chromosome, Particle } from '../types/algorithm';
import { OptimizationParameters, OptimizationResults, OptimizationResultsWithStats, AlgorithmResult, ChartDataPoint } from '../types';
import { parseQWSDataset, buildQualityMatrix } from '../utils/qwsParser';
import { calculateResponseTime, calculateEnergy, calculateCost } from '../utils/fitnessCalculator';
import { runGeneticAlgorithm } from '../algorithms/geneticAlgorithm';
import { runCulturalAlgorithm } from '../algorithms/culturalAlgorithm';
import { runSFLA } from '../algorithms/sfla';
import { runSFLAGA } from '../algorithms/sflaga';
import { runPSO } from '../algorithms/pso';
import { runGAPSO } from '../algorithms/gapso';
import { calculateAlgorithmStatistics } from '../utils/statistics';

export interface ConvergenceData {
  ga: ChartDataPoint[];
  culture: ChartDataPoint[];
  frog: ChartDataPoint[];
  sflaga: ChartDataPoint[];
  pso: ChartDataPoint[];
  gapso: ChartDataPoint[];
}

export class OptimizationService {
  private matrix: number[][][] | null = null;
  private services: any[] = [];
  private taskCount: number = 0;
  private serviceCount: number = 0;

  /**
   * Loads and parses QWS dataset
   */
  loadDataset(csvText: string, taskCount: number): void {
    this.services = parseQWSDataset(csvText);
    this.serviceCount = this.services.length;
    this.taskCount = taskCount;
    this.matrix = buildQualityMatrix(this.services, taskCount);
  }

  /**
   * Runs all optimization algorithms
   */
  async runOptimization(
    parameters: OptimizationParameters,
    onAlgorithmStart?: (algorithm: string) => void,
    onGenerationUpdate?: (algorithm: string, generation: number, fitness: number) => void
  ): Promise<{
    results: OptimizationResults;
    convergenceData: ConvergenceData;
  }> {
    if (!this.matrix) {
      throw new Error('Dataset not loaded');
    }

    const convergenceData: ConvergenceData = {
      ga: [],
      culture: [],
      frog: [],
      sflaga: [],
      pso: [],
      gapso: [],
    };

    // Run GA
    if (onAlgorithmStart) onAlgorithmStart('GA');
    const gaStartTime = performance.now();
    const gaResult = await runGeneticAlgorithm(
      this.taskCount,
      this.serviceCount,
      this.matrix,
      {
        populationSize: parameters.initialPopulation,
        generations: parameters.iterations,
        selectionRate: parameters.selectionRate,
        crossoverRate: parameters.crossoverRate,
        mutationRate: parameters.mutationRate,
      },
      (gen, fitness) => {
        convergenceData.ga.push({ iteration: gen, fitness });
        if (onGenerationUpdate) onGenerationUpdate('GA', gen, fitness);
      }
    );
    const gaExecutionTime = performance.now() - gaStartTime;

    // Run Cultural Algorithm
    if (onAlgorithmStart) onAlgorithmStart('Cultural');
    const cultureStartTime = performance.now();
    const cultureResult = await runCulturalAlgorithm(
      this.taskCount,
      this.serviceCount,
      this.matrix,
      {
        populationSize: parameters.initialPopulation,
        generations: parameters.iterations,
        selectionRate: parameters.selectionRate,
        crossoverRate: parameters.crossoverRate,
        mutationRate: parameters.mutationRate,
      },
      (gen, fitness) => {
        convergenceData.culture.push({ iteration: gen, fitness });
        if (onGenerationUpdate) onGenerationUpdate('Cultural', gen, fitness);
      }
    );
    const cultureExecutionTime = performance.now() - cultureStartTime;

    // Run SFLA
    if (onAlgorithmStart) onAlgorithmStart('SFLA');
    const frogStartTime = performance.now();
    const frogResult = await runSFLA(
      this.taskCount,
      this.serviceCount,
      this.matrix,
      {
        populationSize: parameters.initialPopulation,
        generations: parameters.iterations,
        memeplexCount: parameters.memeplexCount,
      },
      (gen, fitness) => {
        convergenceData.frog.push({ iteration: gen, fitness });
        if (onGenerationUpdate) onGenerationUpdate('SFLA', gen, fitness);
      }
    );
    const frogExecutionTime = performance.now() - frogStartTime;

    // Run SFLAGA
    if (onAlgorithmStart) onAlgorithmStart('SFLAGA');
    const sflagaStartTime = performance.now();
    const sflagaResult = await runSFLAGA(
      this.taskCount,
      this.serviceCount,
      this.matrix,
      {
        populationSize: parameters.initialPopulation,
        generations: parameters.iterations,
        memeplexCount: parameters.memeplexCount,
        crossoverRate: parameters.crossoverRate,
        mutationRate: parameters.mutationRate,
      },
      (gen, fitness) => {
        convergenceData.sflaga.push({ iteration: gen, fitness });
        if (onGenerationUpdate) onGenerationUpdate('SFLAGA', gen, fitness);
      }
    );
    const sflagaExecutionTime = performance.now() - sflagaStartTime;

    // Run PSO
    if (onAlgorithmStart) onAlgorithmStart('PSO');
    const psoStartTime = performance.now();
    const psoResult = await runPSO(
      this.taskCount,
      this.serviceCount,
      this.matrix,
      {
        populationSize: parameters.initialPopulation,
        generations: parameters.iterations,
      },
      (gen, fitness) => {
        convergenceData.pso.push({ iteration: gen, fitness });
        if (onGenerationUpdate) onGenerationUpdate('PSO', gen, fitness);
      }
    );
    const psoExecutionTime = performance.now() - psoStartTime;

    // Run GAPSO
    if (onAlgorithmStart) onAlgorithmStart('GAPSO');
    const gapsoStartTime = performance.now();
    const gapsoResult = await runGAPSO(
      this.taskCount,
      this.serviceCount,
      this.matrix,
      {
        populationSize: parameters.initialPopulation,
        generations: parameters.iterations,
        crossoverRate: parameters.crossoverRate,
      },
      (gen, fitness) => {
        convergenceData.gapso.push({ iteration: gen, fitness });
        if (onGenerationUpdate) onGenerationUpdate('GAPSO', gen, fitness);
      }
    );

    const gapsoExecutionTime = performance.now() - gapsoStartTime;

    // Convert results to common format
    const convertChromosomeToResult = (chrom: Chromosome, execTime: number): AlgorithmResult => ({
      fitness: chrom.fitness,
      solution: chrom.x,
      energy: calculateEnergy(chrom.x, this.matrix!, this.taskCount, this.serviceCount),
      cost: calculateCost(chrom.x, this.matrix!, this.taskCount, this.serviceCount),
      responseTime: calculateResponseTime(chrom.x, this.matrix!, this.taskCount, this.serviceCount),
      executionTime: execTime,
    });

    const convertParticleToResult = (particle: Particle, execTime: number): AlgorithmResult => ({
      fitness: particle.fitness,
      solution: particle.position,
      energy: calculateEnergy(particle.position, this.matrix!, this.taskCount, this.serviceCount),
      cost: calculateCost(particle.position, this.matrix!, this.taskCount, this.serviceCount),
      responseTime: calculateResponseTime(particle.position, this.matrix!, this.taskCount, this.serviceCount),
      executionTime: execTime,
    });

    const results: OptimizationResults = {
      ga: convertChromosomeToResult(gaResult, gaExecutionTime),
      culture: convertChromosomeToResult(cultureResult, cultureExecutionTime),
      frog: convertChromosomeToResult(frogResult, frogExecutionTime),
      sflaga: convertChromosomeToResult(sflagaResult, sflagaExecutionTime),
      pso: convertParticleToResult(psoResult, psoExecutionTime),
      gapso: convertParticleToResult(gapsoResult, gapsoExecutionTime),
      configuration: {
        taskCount: parameters.taskCount,
        propertyCount: parameters.qosProperties,
        initialPopulation: parameters.initialPopulation,
        crossover: parameters.crossoverRate,
        mutation: parameters.mutationRate,
      },
    };

    return { results, convergenceData };
  }

  /**
   * Runs optimization multiple times and calculates statistics
   */
  async runOptimizationWithStatistics(
    parameters: OptimizationParameters,
    onAlgorithmStart?: (algorithm: string) => void,
    onGenerationUpdate?: (algorithm: string, generation: number, fitness: number) => void,
    onRunComplete?: (runNumber: number, totalRuns: number) => void
  ): Promise<{
    results: OptimizationResultsWithStats;
    convergenceData: ConvergenceData;
  }> {
    const numberOfRuns = parameters.numberOfRuns || 1;
    
    if (numberOfRuns === 1) {
      // Single run - no statistics needed
      const { results, convergenceData } = await this.runOptimization(
        parameters,
        onAlgorithmStart,
        onGenerationUpdate
      );
      return {
        results: {
          ...results,
          timestamp: new Date().toISOString(),
          runNumber: 1,
        },
        convergenceData,
      };
    }

    // Multiple runs - collect results and calculate statistics
    const allRuns: OptimizationResults[] = [];
    let lastConvergenceData: ConvergenceData | null = null;

    for (let run = 0; run < numberOfRuns; run++) {
      if (onRunComplete) {
        onRunComplete(run + 1, numberOfRuns);
      }

      const { results, convergenceData } = await this.runOptimization(
        parameters,
        (algorithm) => {
          if (onAlgorithmStart) {
            onAlgorithmStart(`${algorithm} (Run ${run + 1}/${numberOfRuns})`);
          }
        },
        (algorithm, generation, fitness) => {
          if (onGenerationUpdate) {
            // Adjust progress to account for multiple runs
            // Each run contributes (100 / numberOfRuns) to overall progress
            // Within each run, we track generation progress as before
            const runBaseProgress = (run / numberOfRuns) * 100;
            const runProgress = (100 / numberOfRuns) * ((generation / parameters.iterations));
            const totalProgress = runBaseProgress + runProgress;
            // Extract just the algorithm name (remove run info)
            const algName = algorithm.split(' (Run')[0];
            onGenerationUpdate(algName, generation, fitness);
          }
        }
      );

      allRuns.push(results);
      lastConvergenceData = convergenceData; // Store convergence data from last run
    }

    // Calculate statistics for each algorithm
    const statistics: OptimizationResultsWithStats['statistics'] = {
      ga: calculateAlgorithmStatistics(allRuns.map(r => r.ga)),
      culture: calculateAlgorithmStatistics(allRuns.map(r => r.culture)),
      frog: calculateAlgorithmStatistics(allRuns.map(r => r.frog)),
      sflaga: calculateAlgorithmStatistics(allRuns.map(r => r.sflaga)),
      pso: calculateAlgorithmStatistics(allRuns.map(r => r.pso)),
      gapso: calculateAlgorithmStatistics(allRuns.map(r => r.gapso)),
    };

    // Use the last run's results as the representative result
    const lastRun = allRuns[allRuns.length - 1];

    return {
      results: {
        ...lastRun,
        statistics,
        timestamp: new Date().toISOString(),
        runNumber: numberOfRuns,
      },
      convergenceData: lastConvergenceData!,
    };
  }
}

